/**
 * WhatsApp Embedded Signup (Meta) — shared frontend config.
 * Must match Meta App settings and server FACEBOOK_APP_ID / FACEBOOK_APP_SECRET.
 */
export const FACEBOOK_APP_ID =
  process.env.REACT_APP_FACEBOOK_APP_ID || "3650171595140140";

export const EMBEDDED_SIGNUP_CONFIG_ID =
  process.env.REACT_APP_EMBEDDED_SIGNUP_CONFIG_ID || "1546833560465815";

export const FACEBOOK_GRAPH_VERSION =
  process.env.REACT_APP_FACEBOOK_GRAPH_VERSION || "v25.0";

/**
 * Optional override only. Leave empty for Embedded Signup (config_id) popup flow —
 * Meta issues the code without redirect_uri; sending one on token exchange causes OAuth 36008.
 */
export const EMBEDDED_SIGNUP_REDIRECT_URI =
  process.env.REACT_APP_EMBEDDED_SIGNUP_REDIRECT_URI || "";

/**
 * AiSensy Partner Solution ID (Type A TSP). Passing it in extras.setup makes Meta
 * create the customer's WABA under the "Blutec AI" partner solution, so AiSensy
 * can share its credit line and host the number after procurement.
 */
export const AISENSY_SOLUTION_ID =
  process.env.REACT_APP_EMBEDDED_SIGNUP_SOLUTION_ID ||
  process.env.REACT_APP_AISENSY_SOLUTION_ID ||
  "1015630734828860";

function normalizeRedirectUri(uri) {
  try {
    const url = new URL(uri);
    let path = url.pathname || "/";
    if (path.length > 1 && path.endsWith("/")) {
      path = path.slice(0, -1);
    }
    return `${url.origin}${path}`;
  } catch {
    return String(uri || "").trim();
  }
}

/** Same value for FB.login and token exchange, or "" if unused (preferred for config_id flow). */
export function getEmbeddedSignupRedirectUri() {
  const fromEnv = EMBEDDED_SIGNUP_REDIRECT_URI.trim();
  return fromEnv ? normalizeRedirectUri(fromEnv) : "";
}

export function getEmbeddedSignupLoginOptions() {
  const options = {
    config_id: EMBEDDED_SIGNUP_CONFIG_ID,
    response_type: "code",
    override_default_response_type: true,
    // WhatsApp permissions live on Meta → Facebook Login for Business →
    // Configurations → 3259348500922081. Catalog uses AiSensy Direct APIs,
    // so do not add catalog_management / business_management here.
    extras: {
      setup: {
        solutionID: AISENSY_SOLUTION_ID,
      },
      featureType: "whatsapp_business_app_onboarding",
      sessionInfoVersion: "3",
      features: [{ name: "marketing_messages_lite" }],
      version: "v3",
    },
  };

  const redirectUri = getEmbeddedSignupRedirectUri();
  if (redirectUri) {
    options.redirect_uri = redirectUri;
  }

  return options;
}

export function initFacebookSdk() {
  if (!window.FB) return false;

  window.FB.init({
    appId: FACEBOOK_APP_ID,
    cookie: true,
    xfbml: true,
    version: FACEBOOK_GRAPH_VERSION,
  });

  return true;
}

/** In-memory session from Meta WA_EMBEDDED_SIGNUP postMessage (waba_id, phone_number_id). */
let lastEmbeddedSignupSession = null;

const FB_MESSAGE_ORIGINS = new Set([
  "https://www.facebook.com",
  "https://web.facebook.com",
  "https://facebook.com",
]);

export function captureEmbeddedSignupMessage(event) {
  if (!event?.origin || !FB_MESSAGE_ORIGINS.has(event.origin)) return;
  try {
    const payload = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    if (payload?.type !== "WA_EMBEDDED_SIGNUP") return;
    const data = payload.data || {};
    const eventName = payload.event || null;

    if (eventName === "CANCEL") {
      lastEmbeddedSignupSession = {
        ...(lastEmbeddedSignupSession || {}),
        cancelled: true,
        event: eventName,
      };
      return;
    }

    lastEmbeddedSignupSession = {
      wabaId: data.waba_id || data.waba_ids?.[0] || null,
      phoneNumberId: data.phone_number_id || data.phone_number_ids?.[0] || null,
      businessId: data.business_id || null,
      event: eventName,
      cancelled: false,
    };
  } catch {
    // ignore non-JSON postMessages
  }
}

export function getEmbeddedSignupSession() {
  return lastEmbeddedSignupSession;
}

export function clearEmbeddedSignupSession() {
  lastEmbeddedSignupSession = null;
}

/** Wait for Meta postMessage IDs (often arrives just after FB.login callback). */
export function waitForEmbeddedSignupSession(maxMs = 5000) {
  return new Promise((resolve) => {
    const existing = getEmbeddedSignupSession();
    if (existing?.wabaId || existing?.phoneNumberId) {
      resolve(existing);
      return;
    }

    const deadline = Date.now() + maxMs;
    const poll = () => {
      const session = getEmbeddedSignupSession();
      if (session?.wabaId || session?.phoneNumberId || Date.now() >= deadline) {
        resolve(session);
        return;
      }
      setTimeout(poll, 100);
    };
    poll();
  });
}

/** Build backend callback body with optional redirect + Meta session IDs. */
export function buildEmbeddedSignupCallbackBody(code, session = getEmbeddedSignupSession()) {
  const redirectUri = getEmbeddedSignupRedirectUri();
  return {
    code,
    ...(redirectUri ? { redirectUri } : {}),
    ...(session?.wabaId ? { wabaId: session.wabaId } : {}),
    ...(session?.phoneNumberId ? { phoneNumberId: session.phoneNumberId } : {}),
  };
}

/** Async variant — waits briefly for WA_EMBEDDED_SIGNUP postMessage before building body. */
export async function buildEmbeddedSignupCallbackBodyAsync(code, waitMs = 5000) {
  const session = await waitForEmbeddedSignupSession(waitMs);
  if (session?.cancelled) {
    return { cancelled: true };
  }
  return buildEmbeddedSignupCallbackBody(code, session);
}

export function installEmbeddedSignupMessageListener() {
  const handler = (event) => captureEmbeddedSignupMessage(event);
  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}